import { useEffect, useRef, useState } from 'react';
import { Alert, Card, Typography } from 'antd';
import { useAuth } from '../auth/AuthContext';

// Google Identity Services client ID. Without it there is no way into the
// console at all, so an empty value is a misconfiguration rather than a
// variation to degrade gracefully around.
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

/**
 * Sign-in is Google SSO only.
 *
 * A password proves someone knows a secret; it says nothing about which
 * organisation they belong to. Access here is defined by domain — the backend
 * verifies the Google token, enforces the whitelisted domain, and provisions
 * the admin record on first sign-in — so a password form would be a second,
 * weaker door into the same building.
 */
export default function LoginPage() {
  const { loginWithGoogle } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Initialise the Google button once the GIS script (loaded via <script> in
  // index.html) has registered `window.google`.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleButtonRef.current) return;

    function renderButton() {
      if (!window.google?.accounts?.id || !googleButtonRef.current) return;

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          setError(null);
          try {
            await loginWithGoogle(response.credential);
          } catch (err) {
            setError((err as Error).message || 'Google sign-in failed');
          }
        },
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        width: 336,
      });
    }

    if (window.google?.accounts?.id) {
      renderButton();
    } else {
      // The async/defer <script> may not have finished yet — poll briefly.
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval);
          renderButton();
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [loginWithGoogle]);

  return (
    <div className="login-shell">
      <div className="login-panel">
        <LoginBrand />
        <Card className="login-card">
          <Typography.Text strong style={{ fontSize: 15 }}>
            Sign in
          </Typography.Text>
          <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginTop: 4 }}>
            Internal platform administration. Sign in with your Seiki Google
            account &mdash; this is not a customer login.
          </Typography.Paragraph>

          {error && (
            <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
          )}

          {GOOGLE_CLIENT_ID ? (
            <div ref={googleButtonRef} style={{ width: '100%' }} />
          ) : (
            <Alert
              type="warning"
              showIcon
              message="Google sign-in is not configured"
              description="VITE_GOOGLE_CLIENT_ID was empty at build time, so this console has no way to sign anyone in. It has to be set for the environment being built, because Vite inlines it."
            />
          )}
        </Card>
        <div className="login-foot">
          Access is limited to Seiki Google accounts.
        </div>
      </div>
    </div>
  );
}

/** The same lockup the console rail carries, so the two read as one product. */
function LoginBrand() {
  return (
    <div className="login-brand">
      <span className="app-brand-mark">S</span>
      <span className="login-brand-name">Seiki Backoffice</span>
    </div>
  );
}
